import { url, z } from "zod";
import { Params } from "react-router-dom";
import { UUID } from "../../../utils/typeDefinitions";
import IAppDataProvider from "./IAppDataProvider";
import { ConfigurationState } from "../../../store/configurationState/interfaces";
import BimVisualizerApiProvider from "../BimVisualizerApiProvider";

import ApartmentAppDataProvider from "./ApartmentAppDataProvider";
import SpatialStructureAppDataProvider from "./SpatialStructureAppDataProvider";
import GbContextAppDataProvider from "./GbContextAppDataProvider";
import ApiCoreDocumentProvider from "../../documentProvider/ApiCoreDocumentProvider";
import { DataProviderContextEnum } from "../../../enums/DataProviderContextEnum";
import ApiChecklistCoreDocumentProvider from "../../documentProvider/ApiChecklistCoreDocumentProvider";
import CoreApiProvider from "../../coreApi/CoreApiProvider";

const hasGbContext = (urlParams : URLSearchParams) : boolean => {
    // URLSearchParams.get returns null (not undefined) for a missing param
    return urlParams.has('organisationId') && urlParams.has('projectId');
};

const createGbContextAppDataProvider = (    urlParams : URLSearchParams, 
                                            configuration: ConfigurationState,
                                            dataProviderContext: DataProviderContextEnum
                                        ) : IAppDataProvider => {
    
    const gbContext = {
        organisationId: urlParams.get('organisationId') || "",
        tenantId: urlParams.get('tenantId') || "",
        projectId: Number(urlParams.get('projectId') || 0),
        apartmentId: Number(urlParams.get('apartmentId') || 0)
    }

    const token = urlParams.get('token') || import.meta.env.VITE_CORE_API_USER_TOKEN;
    const apiProvider = new BimVisualizerApiProvider(configuration, token);
    const coreApiProvider = new CoreApiProvider(configuration, token);

    const docProvider = dataProviderContext === DataProviderContextEnum.Checklist
                        ? new ApiChecklistCoreDocumentProvider(apiProvider, gbContext)
                        : new ApiCoreDocumentProvider(apiProvider, gbContext);

    return new GbContextAppDataProvider(apiProvider, coreApiProvider, gbContext, docProvider);
}

const createAppDataProvider = (urlParams : URLSearchParams, configuration: ConfigurationState, dataProviderContext: DataProviderContextEnum) : IAppDataProvider => {

    if (hasGbContext(urlParams)) {
        return createGbContextAppDataProvider(urlParams, configuration, dataProviderContext);
    }

    if (!urlParams.get('projectGlobalId'))
        throw new Error("Cannot create loader without project id");

    const apiProvider = new BimVisualizerApiProvider(configuration, urlParams.get('token') || import.meta.env.VITE_CORE_API_USER_TOKEN);

    // For loading the whole floor or building
    if (urlParams.get('spatialStructureGlobalId')) {
        return new SpatialStructureAppDataProvider(apiProvider, urlParams.get('projectGlobalId') || "", urlParams.get('spatialStructureGlobalId') || "");
    }
    
    // For loading a single apartment       
    else if (urlParams.get('apartmentGlobalId')) {
        return new ApartmentAppDataProvider(apiProvider, urlParams.get('projectGlobalId') || "", urlParams.get('apartmentGlobalId') || "");
    }

    throw new Error("Could not create startup loader using given url parameters");
};

export {createAppDataProvider};